import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { AgencyLocationDto } from './api/myAgent.api';

export interface SearchOrigin {
  /** Seeds the "area served" input. */
  readonly area?: string;
  /** Centres the map before any search has run. */
  readonly center?: { readonly lat: number; readonly lon: number };
}

/**
 * The seed for the "area served" field, which the directory matches as a single
 * value — not free text. A combined "City, ST ZIP" string matches nothing
 * (verified: `location=Haneville, CA 93650` returns 0, `location=93650`
 * returns 5), so this sends the ZIP, falling back to the city.
 */
const areaOf = (location?: AgencyLocationDto): string | undefined =>
  location?.zipCode || location?.city || undefined;

/**
 * Where the agency search should start from, in the order the product wants:
 *
 * 1. the hard-linked agency's location — the policyholder's own agent;
 * 2. the browser's geolocation, when they allow it;
 * 3. the policyholder account's own address.
 *
 * Geolocation is asynchronous and permission-gated, so this returns the account
 * fallback immediately and upgrades to the browser position if one arrives. It
 * is never requested when a linked agency already answers the question — no
 * point prompting for a permission whose answer we would discard — nor while
 * `enabled` is false, which the page passes when the search is not shown.
 */
export function useSearchOrigin(
  agencyLocation?: AgencyLocationDto,
  enabled = true
): SearchOrigin {
  const account = useSelector(
    (state: any) => state?.account?.policyHolderAccount
  );
  const [browserCenter, setBrowserCenter] = useState<
    { lat: number; lon: number } | undefined
  >(undefined);

  const linkedCenter = agencyLocation?.location;
  const linkedArea = areaOf(agencyLocation);
  // An agency is "linked" once it can seed the search at all. AgencyDto carries
  // a ZIP and city but no geo point, so requiring coordinates treated a linked
  // agency as absent — falling through to the account and firing the browser's
  // location prompt for an answer we would not have used.
  const hasLinkedAgency = !!linkedArea || typeof linkedCenter?.lat === 'number';

  useEffect(() => {
    if (!enabled || hasLinkedAgency || !navigator?.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        setBrowserCenter({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        }),
      // Denied or unavailable is an ordinary outcome, not an error worth
      // surfacing — the account address still seeds the search.
      () => undefined
    );
  }, [enabled, hasLinkedAgency]);

  if (hasLinkedAgency) {
    return { area: linkedArea, center: linkedCenter };
  }

  // Accounts do not all carry a ZIP; city and state still narrow the directory.
  const accountArea = account?.zipCode || account?.city || account?.state;

  if (browserCenter) {
    return { area: accountArea, center: browserCenter };
  }

  return { area: accountArea, center: account?.location };
}

export default useSearchOrigin;
